/**
 * O que faz: Busca o agregado de follow-up da clínica no período via RPC.
 * Onde usa: FollowupCampaignMetrics na aba Follow-up.
 * Conexões: supabase.rpc('followup_analytics') e parser em features/followup/analytics.
 * Tabelas: nenhuma no cliente; a RPC lê follow_up_campaigns, follow_up_execucoes e follow_up_templates.
 * Data: 02/09/2026
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseFollowupAnalytics, payloadVazioFollowupAnalytics } from '@/features/followup/analytics';

export interface UseFollowupAnalyticsOptions {
  startDate?: Date | null;
  endDate?: Date | null;
  clinicaId?: string | null;
  enabled?: boolean;
}

export const useFollowupAnalytics = ({
  startDate = null,
  endDate = null,
  clinicaId = null,
  enabled = true,
}: UseFollowupAnalyticsOptions = {}) => {
  const startIso = startDate ? startDate.toISOString() : null;
  const endIso = endDate ? endDate.toISOString() : null;

  return useQuery({
    queryKey: ['followup-analytics', startIso, endIso, clinicaId ?? 'self'],
    enabled: enabled && Boolean(startIso) && Boolean(endIso),
    queryFn: async () => {
      // RPC: followup_analytics | Operação: SELECT agregado
      // Parâmetros: p_inicio, p_fim, p_clinica_id, p_fuso America/Sao_Paulo
      // Filtros: clínica efetiva dentro da função; intervalo inclusivo
      // Riscos: devolve totais operacionais da clínica; RLS/INVOKER bloqueia outras contas
      const { data, error } = await supabase.rpc('followup_analytics' as never, {
        p_inicio: startIso,
        p_fim: endIso,
        p_clinica_id: clinicaId,
        p_fuso: 'America/Sao_Paulo',
      });

      if (error) {
        throw new Error(`Erro ao carregar analytics de follow-up: ${error.message}`);
      }

      return parseFollowupAnalytics(data);
    },
    placeholderData: payloadVazioFollowupAnalytics(),
    staleTime: 60 * 1000,
  });
};
